import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { message, TimePicker, Form, Switch } from 'antd';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faShieldAlt, 
    faEye, 
    faBell, 
    faClock, 
    faLock,
    faEnvelope,
    faCalendar,
    faBold,
    faItalic
} from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import '../../../styles/settings/settings-common.css';
import '../../../styles/settings/prefs.css';

const PREF_SECTION_IDS = ['security', 'profile', 'notifications', 'mfa'];
const USER_SETTINGS_AUTOSAVE_DEBOUNCE_MS = 2000;

const parsePrefsHash = (hash) => {
    if (!hash) return 'security';
    const sub = hash.startsWith('prefs-') ? hash.slice(6) : hash;
    return PREF_SECTION_IDS.includes(sub) ? sub : 'security';
};

const UserSettings = ({ embedded, unifiedPage }) => {
    const { t, i18n } = useTranslation();
    const skipSaveAfterLanguageChangeRef = useRef(false);
    const location = useLocation();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [, setSettings] = useState(null);
    const hashRaw = location.hash ? location.hash.slice(1) : '';

    const [activeSectionState, setActiveSectionState] = useState(() => parsePrefsHash(hashRaw || (typeof window !== 'undefined' && window.location.hash ? window.location.hash.slice(1) : '')));
    const activeSection = unifiedPage ? parsePrefsHash(hashRaw) : activeSectionState;
    const [formData, setFormData] = useState({});
    const signatureEditorRef = useRef(null);
    const signatureSyncRef = useRef(false);
    const contentRef = useRef(null);
    const [signatureFont, setSignatureFont] = useState('');
    const [signatureSize, setSignatureSize] = useState('medium');

    const sanitizeSignatureHtml = (html) => {
        if (!html || !String(html).trim()) return '';
        const div = document.createElement('div');
        div.innerHTML = html;
        const allowed = ['B', 'I', 'SPAN', 'BR'];
        const walk = (node) => {
            if (node.nodeType === Node.TEXT_NODE) return node.cloneNode(true);
            if (node.nodeType === Node.ELEMENT_NODE) {
                const tag = node.tagName.toUpperCase();
                if (!allowed.includes(tag)) {
                    const frag = document.createDocumentFragment();
                    node.childNodes.forEach(c => { const s = walk(c); if (s) frag.appendChild(s); });
                    return frag;
                }
                const el = document.createElement(tag);
                if (tag === 'SPAN' && node.style) {
                    if (node.style.fontFamily) el.style.fontFamily = node.style.fontFamily;
                    if (node.style.fontSize) el.style.fontSize = node.style.fontSize;
                }
                node.childNodes.forEach(c => { const s = walk(c); if (s) el.appendChild(s); });
                return el;
            }
            return null;
        };
        const frag = document.createDocumentFragment();
        div.childNodes.forEach(c => { const s = walk(c); if (s) frag.appendChild(s); });
        const out = document.createElement('div');
        out.appendChild(frag);
        return out.innerHTML;
    };

    const syncSignatureFromEditor = () => {
        const el = signatureEditorRef.current;
        if (!el) return;
        const html = el.innerHTML;
        const sanitized = sanitizeSignatureHtml(html);
        if (sanitized.length <= 500) {
            signatureSyncRef.current = true;
            handleInputChange('email_signature', sanitized);
        } else {
            el.innerHTML = formData.email_signature || '';
        }
        el.classList.toggle('is-empty', !el.textContent.trim());
    };

    const applySignatureFormat = (type, value) => {
        const el = signatureEditorRef.current;
        if (!el) return;
        el.focus();
        const sel = window.getSelection();
        const selected = sel.toString();
        if (type === 'bold') {
            document.execCommand('bold', false, null);
        } else if (type === 'italic') {
            document.execCommand('italic', false, null);
        } else if (type === 'font' && value) {
            document.execCommand('insertHTML', false, `<span style="font-family:${value}">${selected}</span>`);
        } else if (type === 'size' && value) {
            const sizes = { small: '12px', medium: '14px', large: '18px' };
            const px = sizes[value] || '14px';
            document.execCommand('insertHTML', false, `<span style="font-size:${px}">${selected}</span>`);
        }
        syncSignatureFromEditor();
    };

    useEffect(() => {
        const el = signatureEditorRef.current;
        const html = formData.email_signature || '';
        if (!el || signatureSyncRef.current) {
            signatureSyncRef.current = false;
            return;
        }
        if (el.innerHTML !== html) {
            el.innerHTML = html;
        }
        el.classList.toggle('is-empty', !el.textContent.trim());
    }, [formData.email_signature]);

    useEffect(() => {
        fetchUserSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!embedded || unifiedPage) return;
        if (initialLoading) return;
        const container = contentRef.current;
        const hash = location.hash ? location.hash.slice(1) : '';
        const sub = parsePrefsHash(hash);
        if (PREF_SECTION_IDS.includes(sub)) {
            const el = document.getElementById(`user-settings-section-${sub}`);
            if (el && container) {
                const targetTop = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - 12;
                container.scrollTo({ top: Math.max(targetTop, 0), behavior: 'smooth' });
            } else if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }, [embedded, unifiedPage, location.hash, initialLoading]);

    useEffect(() => {
        const handler = () => {
            skipSaveAfterLanguageChangeRef.current = true;
        };
        i18n.on('languageChanged', handler);
        return () => { i18n.off('languageChanged', handler); };
    }, [i18n]);

    useEffect(() => {
        if (!embedded || unifiedPage) return;
        const container = contentRef.current;
        if (!container) return;
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        const id = entry.target.id?.replace('user-settings-section-', '');
                        if (PREF_SECTION_IDS.includes(id)) setActiveSectionState(id);
                        break;
                    }
                }
            },
            { root: container, rootMargin: '-72px 0px -48% 0px', threshold: 0.05 }
        );
        PREF_SECTION_IDS.forEach(id => {
            const el = document.getElementById(`user-settings-section-${id}`);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, [embedded, unifiedPage]);

    const fetchUserSettings = async () => {
        try {
            setInitialLoading(true);
            const response = await fetch('/api/user-settings/', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const data = await response.json();
            
            if (data.success) {
                setSettings(data.settings);
                
                // Convert daily_expiration_time string to dayjs object for TimePicker
                let dailyExpirationTime = null;
                if (data.settings.message_security.daily_expiration_time) {
                    const timeString = data.settings.message_security.daily_expiration_time;
                    // Parse time string (HH:MM:SS format) and create dayjs object
                    dailyExpirationTime = dayjs(timeString, 'HH:mm:ss');
                }
                
                setFormData({
                    message_lifetime_days: data.settings.message_security.message_lifetime_days,
                    daily_expiration_time: dailyExpirationTime,
                    display_name: data.settings.profile_settings.display_name,
                    email_signature: data.settings.profile_settings.email_signature,
                    email_notifications_enabled: data.settings.notification_settings.email_notifications_enabled,
                    activity_notifications_enabled: data.settings.notification_settings.activity_notifications_enabled,
                    recipient_read_notifications: data.settings.notification_settings.recipient_read_notifications,
                    expiry_notifications: data.settings.notification_settings.expiry_notifications,
                    mfa_enabled: data.settings.security_settings.mfa_enabled,
                    mfa_email_enabled: data.settings.security_settings.mfa_email_enabled,
                    mfa_authenticator_enabled: data.settings.security_settings.mfa_authenticator_enabled,
                });
            } else {
                message.error(data.error || t('userSettings.errors.loadFailed'));
            }
        } catch (error) {
            console.error('Error fetching user settings:', error);
            message.error(t('userSettings.errors.loadFailed'));
        } finally {
            setInitialLoading(false);
        }
    };

    const handleSubmit = async () => {
        const activeEl = document.activeElement;
        try {
            setLoading(true);
            
            // Convert dayjs time object to HH:MM:SS string for backend
            let dailyExpirationTimeString = '';
            if (formData.daily_expiration_time && dayjs.isDayjs(formData.daily_expiration_time)) {
                dailyExpirationTimeString = formData.daily_expiration_time.format('HH:mm:ss');
            }
            
            const updateData = {
                message_security: {
                    message_lifetime_days: formData.message_lifetime_days,
                    daily_expiration_time: dailyExpirationTimeString,
                },
                profile_settings: {
                    display_name: formData.display_name,
                    email_signature: formData.email_signature,
                },
                privacy_settings: {},
                notification_settings: {
                    email_notifications_enabled: formData.email_notifications_enabled,
                    activity_notifications_enabled: formData.activity_notifications_enabled,
                    recipient_read_notifications: formData.recipient_read_notifications,
                    expiry_notifications: formData.expiry_notifications,
                },
                security_settings: {
                    mfa_enabled: formData.mfa_enabled,
                    mfa_email_enabled: formData.mfa_email_enabled,
                    mfa_authenticator_enabled: formData.mfa_authenticator_enabled,
                },
            };

            const response = await fetch('/api/user-settings/update/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(updateData),
            });

            const data = await response.json();
            
            if (data.success) {
                message.success(t('userSettings.updateSuccess'));
                setSettings(data.settings);
            } else {
                message.error(data.error || t('userSettings.errors.updateFailed'));
            }
        } catch (error) {
            console.error('Error updating settings:', error);
            message.error(t('userSettings.errors.updateFailed'));
        } finally {
            setLoading(false);
            if (activeEl && typeof activeEl.focus === 'function' && document.body.contains(activeEl)) {
                requestAnimationFrame(() => requestAnimationFrame(() => activeEl.focus({ preventScroll: true })));
            }
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleToggleChange = (field) => {
        setFormData(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const hasInitializedRef = useRef(false);
    useEffect(() => {
        if (initialLoading) return;
        if (!hasInitializedRef.current) {
            hasInitializedRef.current = true;
            return;
        }
        if (skipSaveAfterLanguageChangeRef.current) {
            skipSaveAfterLanguageChangeRef.current = false;
            return;
        }
        const timer = setTimeout(() => handleSubmit(), USER_SETTINGS_AUTOSAVE_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [
        formData.message_lifetime_days,
        formData.daily_expiration_time,
        formData.display_name,
        formData.email_signature,
        formData.email_notifications_enabled,
        formData.activity_notifications_enabled,
        formData.recipient_read_notifications,
        formData.expiry_notifications,
        formData.mfa_enabled,
        formData.mfa_email_enabled,
        formData.mfa_authenticator_enabled,
    ]);

    if (initialLoading) {
        return (
            <div className="user-settings-container">
            </div>
        );
    }

    return (
        <div className={`user-settings-container ${embedded ? 'user-settings-embedded' : ''} ${unifiedPage ? 'user-settings-unified' : ''}`}>
            <div className="user-settings-card">
                <div className="user-settings-split">
                    <div className="user-settings-main">
                        <div className={`user-settings-scroll-area ${unifiedPage ? 'user-settings-scroll-area-unified' : ''}`} ref={contentRef} data-settings-scroll>
                        <Form form={form} onFinish={handleSubmit} layout="vertical">
                            <div className="user-settings-content">
                        <div id="user-settings-section-security" className="user-settings-section user-settings-scroll-section">
                                <h2 className="user-settings-section-title">{t('userSettings.tabs.security.label')}</h2>
                                <div className="user-settings-card-layout">
                                    {/* Message Lifetime Card */}
                                    <div className="user-settings-card-item">
                                        <div className="user-settings-card-header">
                                            <FontAwesomeIcon icon={faCalendar} className="user-settings-card-icon" />
                                            <div className="user-settings-card-title-section">
                                                <h3 className="user-settings-card-title">{t('userSettings.security.messageLifetimeTitle')}</h3>
                                                <p className="user-settings-card-description">{t('userSettings.security.messageLifetimeDescription')}</p>
                                            </div>
                                        </div>
                                        
                                        <div className="user-settings-card-content">
                                            <div className="user-settings-slider-header">
                                                <label className="user-settings-slider-label">{t('userSettings.security.daysUntilExpiration')}</label>
                                                <div className="user-settings-slider-value-display">
                                                    <span className="user-settings-slider-value">{formData.message_lifetime_days || 1}</span>
                                                    <span className="user-settings-slider-unit">
                                                        {(formData.message_lifetime_days || 1) === 1 ? t('userSettings.security.day') : t('userSettings.security.days')}
                                                    </span>
                                                </div>
                                            </div>
                                            
                                            <div className="user-settings-slider-container">
                                                <input
                                                    type="range"
                                                    min="1"
                                                    max="30"
                                                    value={formData.message_lifetime_days || 1}
                                                    onChange={(e) => handleInputChange('message_lifetime_days', parseInt(e.target.value))}
                                                    className="user-settings-slider"
                                                    style={{
                                                        background: `linear-gradient(to right, #333 0%, #333 ${((formData.message_lifetime_days || 1) - 1) / 29 * 100}%, #d9d9d9 ${((formData.message_lifetime_days || 1) - 1) / 29 * 100}%, #d9d9d9 100%)`
                                                    }}
                                                    disabled={loading}
                                                />
                                                <div className="user-settings-slider-labels">
                                                    <span>1 {t('userSettings.security.day')}</span>
                                                    <span>30 {t('userSettings.security.days')}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Daily Expiration Time Card */}
                                    <div className="user-settings-card-item">
                                        <div className="user-settings-card-header user-settings-card-header-with-input">
                                            <FontAwesomeIcon icon={faClock} className="user-settings-card-icon" />
                                            <div className="user-settings-card-title-section">
                                                <h3 className="user-settings-card-title">{t('userSettings.security.dailyExpiryTimeTitle')}</h3>
                                                <p className="user-settings-card-description">{t('userSettings.security.dailyExpiryTimeDescription')}</p>
                                            </div>
                                            <div className="user-settings-card-input-section">
                                                <label className="user-settings-inline-label">{t('userSettings.security.expirationTime')}</label>
                                                <div className="user-settings-time-picker-wrapper">
                                                    <TimePicker
                                                        value={formData.daily_expiration_time}
                                                        onChange={(time) => handleInputChange('daily_expiration_time', time)}
                                                        format="hh:mm A"
                                                        use12Hours
                                                        placeholder={t('userSettings.security.dailyExpiryTimePlaceholder')}
                                                        style={{ width: '100%' }}
                                                        showNow={false}
                                                        allowClear={false}
                                                        disabled={loading}
                                                    />
                                                </div>
                                                <span className="user-settings-field-hint">{t('userSettings.security.localTimezone')}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        <div id="user-settings-section-profile" className="user-settings-section user-settings-profile-section user-settings-scroll-section">
                                <h2 className="user-settings-section-title">{t('userSettings.tabs.profile.label')}</h2>
                                <div className="user-settings-form-container">
                                    <div className="user-settings-form-grid">
                                        <div className="user-settings-form-group user-settings-form-group-full-width">
                                            <label>{t('userSettings.profile.displayName')}</label>
                                            <div className="user-settings-row-control">
                                                <input
                                                    type="text"
                                                    placeholder={t('userSettings.profile.displayNamePlaceholder')}
                                                    value={formData.display_name || ''}
                                                    onChange={(e) => handleInputChange('display_name', e.target.value)}
                                                    disabled={loading}
                                                />
                                                <span className="user-settings-field-hint">{t('userSettings.profile.displayNameHint')}</span>
                                            </div>
                                        </div>
                                        
                                        <div className="user-settings-form-group user-settings-form-group-full-width">
                                            <label>{t('userSettings.profile.emailSignature')}</label>
                                            <div className="user-settings-row-control">
                                                <div className="user-settings-signature-block">
                                                <div className="user-settings-signature-toolbar">
                                                    <button type="button" className="user-settings-toolbar-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => applySignatureFormat('bold')} disabled={loading} title={t('userSettings.profile.signatureBold')}>
                                                        <FontAwesomeIcon icon={faBold} />
                                                    </button>
                                                    <button type="button" className="user-settings-toolbar-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => applySignatureFormat('italic')} disabled={loading} title={t('userSettings.profile.signatureItalic')}>
                                                        <FontAwesomeIcon icon={faItalic} />
                                                    </button>
                                                    <span className="user-settings-toolbar-sep" />
                                                    <div className="user-settings-toolbar-dropdowns">
                                                        <select className="user-settings-toolbar-select" value={signatureFont} onChange={(e) => { const v = e.target.value; setSignatureFont(v); if (v) applySignatureFormat('font', v); }} disabled={loading} title={t('userSettings.profile.signatureFont')}>
                                                            <option value="">{t('userSettings.profile.signatureFontDefault')}</option>
                                                            <option value="Arial">Arial</option>
                                                            <option value="Georgia">Georgia</option>
                                                            <option value="Times New Roman">Times New Roman</option>
                                                            <option value="Courier New">Courier New</option>
                                                            <option value="Verdana">Verdana</option>
                                                        </select>
                                                        <select className="user-settings-toolbar-select" value={signatureSize} onChange={(e) => { const v = e.target.value; setSignatureSize(v); applySignatureFormat('size', v); }} disabled={loading} title={t('userSettings.profile.signatureSize')}>
                                                            <option value="small">{t('userSettings.profile.signatureSizeSmall')}</option>
                                                            <option value="medium">{t('userSettings.profile.signatureSizeNormal')}</option>
                                                            <option value="large">{t('userSettings.profile.signatureSizeLarge')}</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div
                                                    ref={signatureEditorRef}
                                                    contentEditable={!loading}
                                                    suppressContentEditableWarning
                                                    data-placeholder={t('userSettings.profile.emailSignaturePlaceholder')}
                                                    onInput={syncSignatureFromEditor}
                                                    onPaste={(e) => {
                                                        e.preventDefault();
                                                        const raw = e.clipboardData.getData('text/plain');
                                                        const div = document.createElement('div');
                                                        div.textContent = raw;
                                                        document.execCommand('insertHTML', false, div.innerHTML);
                                                        syncSignatureFromEditor();
                                                    }}
                                                    className="user-settings-signature-editor"
                                                />
                                                <div className="user-settings-field-footer">
                                                    <span className="user-settings-field-hint">{t('userSettings.profile.emailSignatureHint')}</span>
                                                    <span className="user-settings-char-count">{(formData.email_signature || '').length}/500</span>
                                                </div>
                                            </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        <div id="user-settings-section-notifications" className="user-settings-section user-settings-scroll-section">
                                <h2 className="user-settings-section-title">{t('userSettings.tabs.notifications.label')}</h2>
                                <div className="user-settings-card-layout">
                                    <div 
                                        className="user-settings-card-item user-settings-card-clickable"
                                        onClick={() => !loading && handleToggleChange('email_notifications_enabled')}
                                    >
                                        <div className="user-settings-card-header">
                                            <FontAwesomeIcon icon={faEnvelope} className="user-settings-card-icon" />
                                            <div className="user-settings-card-title-section">
                                                <h3 className="user-settings-card-title">{t('userSettings.notifications.email')}</h3>
                                                <p className="user-settings-card-description">{t('userSettings.notifications.emailDescription')}</p>
                                            </div>
                                            <Switch
                                                checked={formData.email_notifications_enabled || false}
                                                onChange={() => handleToggleChange('email_notifications_enabled')}
                                                disabled={loading}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                    </div>
                                    
                                    <div 
                                        className="user-settings-card-item user-settings-card-clickable"
                                        onClick={() => !loading && handleToggleChange('activity_notifications_enabled')}
                                    >
                                        <div className="user-settings-card-header">
                                            <FontAwesomeIcon icon={faBell} className="user-settings-card-icon" />
                                            <div className="user-settings-card-title-section">
                                                <h3 className="user-settings-card-title">{t('userSettings.notifications.activity')}</h3>
                                                <p className="user-settings-card-description">{t('userSettings.notifications.activityDescription')}</p>
                                            </div>
                                            <Switch
                                                checked={formData.activity_notifications_enabled || false}
                                                onChange={() => handleToggleChange('activity_notifications_enabled')}
                                                disabled={loading}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                    </div>
                                    
                                    <div 
                                        className="user-settings-card-item user-settings-card-clickable"
                                        onClick={() => !loading && handleToggleChange('recipient_read_notifications')}
                                    >
                                        <div className="user-settings-card-header">
                                            <FontAwesomeIcon icon={faEye} className="user-settings-card-icon" />
                                            <div className="user-settings-card-title-section">
                                                <h3 className="user-settings-card-title">{t('userSettings.notifications.readReceipts')}</h3>
                                                <p className="user-settings-card-description">{t('userSettings.notifications.readReceiptsDescription')}</p>
                                            </div>
                                            <Switch
                                                checked={formData.recipient_read_notifications || false}
                                                onChange={() => handleToggleChange('recipient_read_notifications')}
                                                disabled={loading}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                    </div>
                                    
                                    <div 
                                        className="user-settings-card-item user-settings-card-clickable"
                                        onClick={() => !loading && handleToggleChange('expiry_notifications')}
                                    >
                                        <div className="user-settings-card-header">
                                            <FontAwesomeIcon icon={faClock} className="user-settings-card-icon" />
                                            <div className="user-settings-card-title-section">
                                                <h3 className="user-settings-card-title">{t('userSettings.notifications.expiry')}</h3>
                                                <p className="user-settings-card-description">{t('userSettings.notifications.expiryDescription')}</p>
                                            </div>
                                            <Switch
                                                checked={formData.expiry_notifications || false}
                                                onChange={() => handleToggleChange('expiry_notifications')}
                                                disabled={loading}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        <div id="user-settings-section-mfa" className="user-settings-section user-settings-scroll-section">
                                <h2 className="user-settings-section-title">{t('userSettings.tabs.mfa.label')}</h2>
                                <div className="user-settings-card-layout">
                                    <div 
                                        className="user-settings-card-item user-settings-card-clickable"
                                        onClick={() => !loading && handleToggleChange('mfa_enabled')}
                                    >
                                        <div className="user-settings-card-header">
                                            <FontAwesomeIcon icon={faLock} className="user-settings-card-icon" />
                                            <div className="user-settings-card-title-section">
                                                <h3 className="user-settings-card-title">{t('userSettings.mfa.enable')}</h3>
                                                <p className="user-settings-card-description">{t('userSettings.mfa.enableDescription')}</p>
                                            </div>
                                            <Switch
                                                checked={formData.mfa_enabled || false}
                                                onChange={() => handleToggleChange('mfa_enabled')}
                                                disabled={loading}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                    </div>
                                    
                                    <div 
                                        className="user-settings-card-item user-settings-card-clickable"
                                        onClick={() => !loading && handleToggleChange('mfa_email_enabled')}
                                    >
                                        <div className="user-settings-card-header">
                                            <FontAwesomeIcon icon={faEnvelope} className="user-settings-card-icon" />
                                            <div className="user-settings-card-title-section">
                                                <h3 className="user-settings-card-title">{t('userSettings.mfa.email')}</h3>
                                                <p className="user-settings-card-description">{t('userSettings.mfa.emailDescription')}</p>
                                            </div>
                                            <Switch
                                                checked={formData.mfa_email_enabled || false}
                                                onChange={() => handleToggleChange('mfa_email_enabled')}
                                                disabled={loading}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                    </div>
                                    
                                    <div 
                                        className="user-settings-card-item user-settings-card-clickable"
                                        onClick={() => !loading && handleToggleChange('mfa_authenticator_enabled')}
                                    >
                                        <div className="user-settings-card-header">
                                            <FontAwesomeIcon icon={faShieldAlt} className="user-settings-card-icon" />
                                            <div className="user-settings-card-title-section">
                                                <h3 className="user-settings-card-title">{t('userSettings.mfa.authenticator')}</h3>
                                                <p className="user-settings-card-description">{t('userSettings.mfa.authenticatorDescription')}</p>
                                            </div>
                                            <Switch
                                                checked={formData.mfa_authenticator_enabled || false}
                                                onChange={() => handleToggleChange('mfa_authenticator_enabled')}
                                                disabled={loading}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            </div>
                        </Form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserSettings; 